import React, { FC, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { useGenericEventsLogger } from '@wix/data-tools-generic-events';
import {
  Button,
  ButtonPriority,
  Checkbox,
  Dialog,
  Divider,
  MobileDrawer,
  PaddingModes,
  Range,
  Tags,
  TagsAlignment,
  TagsSize,
  TagsSkin,
  Text,
  TextButton,
  TextButtonPriority,
  TextTypography as TYPOGRAPHY,
  ToggleSwitch,
} from 'wix-ui-tpa/cssVars';
import { Dismiss } from '@wix/wix-ui-icons-common';
import { classes } from './FiltersModal.st.css';
import { RentalsListDataHooks } from '../dataHooks';
import type {
  AttributeDefinition,
  AttributeSelection,
  FilterBarState,
} from '../../../../types/types';

export type FiltersModalProps = {
  popoverLayer: HTMLElement | null;
  isOpen: boolean;
  onClose: () => void;
  filterState: FilterBarState;
  attributeDefinitions: AttributeDefinition[];
  onApply: (next: FilterBarState) => void;
};

const emptyAttributes: AttributeSelection = {
  booleans: [],
  enums: {},
  numbers: {},
};

export const FiltersModal: FC<FiltersModalProps> = ({
  popoverLayer,
  isOpen,
  onClose,
  filterState,
  attributeDefinitions,
  onApply,
}) => {
  const { t } = useTranslation();
  const { isMobile, isRTL } = useEnvironment();
  const logger = useGenericEventsLogger();

  // Edits are held as a draft and only committed on Apply, so closing the modal
  // discards them — matching the builder component's behaviour.
  const [draft, setDraft] = useState<FilterBarState>(filterState);
  useEffect(() => {
    if (isOpen) {
      setDraft(filterState);
    }
  }, [isOpen, filterState]);

  const booleanDefs = attributeDefinitions.filter(
    (d) => d.valueType === 'BOOLEAN',
  );
  const enumDefs = attributeDefinitions.filter((d) => d.valueType === 'ENUM');
  const numberDefs = attributeDefinitions.filter(
    (d) => d.valueType === 'NUMBER',
  );

  const hasActiveFilters =
    draft.attributes.booleans.length > 0 ||
    Object.keys(draft.attributes.enums).length > 0 ||
    Object.keys(draft.attributes.numbers).length > 0 ||
    draft.rentalBasis.length > 0 ||
    draft.paymentOptions.length > 0;

  const toggleBoolean = (id: string) => {
    const current = draft.attributes.booleans;
    const next = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id];
    logger.userInput({
      componentName: 'attribute_boolean_filter',
      inputNewValue: next.join(','),
      inputPrevValue: current.join(','),
    });
    setDraft((d) => ({
      ...d,
      attributes: { ...d.attributes, booleans: next },
    }));
  };

  /** Selecting the active option again clears that attribute. */
  const selectEnum = (defId: string, optionId: string) => {
    const isSelected = draft.attributes.enums[defId] === optionId;
    logger.userInput({
      componentName: 'attribute_enum_filter',
      inputNewValue: isSelected ? '' : optionId,
      inputPrevValue: draft.attributes.enums[defId] ?? '',
    });
    setDraft((d) => {
      // Rebuilt without the key rather than deleted, so the selection stays a
      // plain value swap and the object keeps a stable shape.
      const enums = Object.fromEntries(
        Object.entries(d.attributes.enums).filter(([id]) => id !== defId),
      );
      if (!isSelected) {
        enums[defId] = optionId;
      }
      return { ...d, attributes: { ...d.attributes, enums } };
    });
  };

  const setNumberRange = (defId: string, range: [number, number]) => {
    logger.userInput({
      componentName: 'attribute_number_filter',
      inputNewValue: range.join(','),
    });
    setDraft((d) => ({
      ...d,
      attributes: {
        ...d.attributes,
        numbers: { ...d.attributes.numbers, [defId]: range },
      },
    }));
  };

  const toggleInList = (
    field: 'rentalBasis' | 'paymentOptions',
    id: string,
  ) => {
    const current = draft[field];
    const next = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id];
    logger.userInput({
      componentName:
        field === 'rentalBasis'
          ? 'rental_basis_filter'
          : 'payment_options_filter',
      inputNewValue: next.join(','),
      inputPrevValue: current.join(','),
    });
    setDraft((d) => ({ ...d, [field]: next }));
  };

  const checkboxSection = (
    title: string,
    field: 'rentalBasis' | 'paymentOptions',
    options: { id: string; label: string }[],
    dataHook: string,
  ) => (
    <section className={classes.section}>
      <Text
        className={classes.sectionTitle}
        typography={TYPOGRAPHY.smallTitle}
        tagName="h2"
      >
        {title}
      </Text>
      <div className={classes.checkboxList}>
        {options.map((option) => (
          <Checkbox
            key={option.id}
            className={classes.checkbox}
            label={option.label}
            checked={draft[field].includes(option.id)}
            onChange={() => toggleInList(field, option.id)}
            data-hook={dataHook}
          />
        ))}
      </div>
    </section>
  );

  const sections: React.ReactNode[] = [
    <section
      key="results-preferences"
      className={classes.section}
      data-hook={RentalsListDataHooks.resultsPreferences}
    >
      <Text
        className={classes.sectionTitle}
        typography={TYPOGRAPHY.smallTitle}
        tagName="h2"
      >
        {t('widget.servicesList.filters.resultsPreferencesTitle')}
      </Text>
      <div className={classes.toggleRow}>
        <ToggleSwitch
          checked={draft.showUnavailable}
          onChange={() => {
            logger.userInput({
              componentName: 'show_unavailable_toggle',
              inputNewValue: String(!draft.showUnavailable),
              inputPrevValue: String(draft.showUnavailable),
            });
            setDraft((d) => ({ ...d, showUnavailable: !d.showUnavailable }));
          }}
          data-hook={RentalsListDataHooks.resultsAvailabilityToggle}
        />
        <Text
          className={classes.toggleLabel}
          typography={TYPOGRAPHY.runningText}
        >
          {t('widget.servicesList.filters.showUnavailableRentals')}
        </Text>
      </div>
    </section>,

    booleanDefs.length > 0 && (
      <section className={classes.section} key="features">
        <Text
          className={classes.sectionTitle}
          typography={TYPOGRAPHY.smallTitle}
          tagName="h2"
        >
          {t('widget.servicesList.filters.featuresTitle')}
        </Text>
        <Tags
          className={classes.chips}
          size={TagsSize.medium}
          alignment={isRTL ? TagsAlignment.right : TagsAlignment.left}
          skin={TagsSkin.light}
          items={booleanDefs.map((def) => ({
            title: def.name,
            value: def.id,
            checked: draft.attributes.booleans.includes(def.id),
          }))}
          onClick={(item) => toggleBoolean(item.value)}
          data-hook={RentalsListDataHooks.attributeBooleanChip}
        />
      </section>
    ),

    ...enumDefs.map((def) => (
      <section className={classes.section} key={def.id}>
        <Text
          className={classes.sectionTitle}
          typography={TYPOGRAPHY.smallTitle}
          tagName="h2"
        >
          {def.name}
        </Text>
        <Tags
          className={classes.chips}
          size={TagsSize.medium}
          alignment={isRTL ? TagsAlignment.right : TagsAlignment.left}
          skin={TagsSkin.light}
          items={(def.options ?? []).map((option) => ({
            title: option.label,
            value: option.id,
            checked: draft.attributes.enums[def.id] === option.id,
          }))}
          onClick={(item) => selectEnum(def.id, item.value)}
          data-hook={RentalsListDataHooks.attributeEnumChip}
        />
      </section>
    )),

    ...numberDefs.map((def) => {
      const min = def.min ?? 0;
      const max = def.max ?? 100;
      const current = draft.attributes.numbers[def.id] ?? [min, max];
      return (
        <section className={classes.section} key={def.id}>
          <Text
            className={classes.sectionTitle}
            typography={TYPOGRAPHY.smallTitle}
            tagName="h2"
          >
            {def.name}
          </Text>
          <Range
            className={classes.range}
            min={min}
            max={max}
            value={current}
            onChange={(value) => setNumberRange(def.id, [value[0], value[1]])}
            minAriaLabel={t('widget.servicesList.filters.rangeMinAriaLabel', {
              name: def.name,
            })}
            maxAriaLabel={t('widget.servicesList.filters.rangeMaxAriaLabel', {
              name: def.name,
            })}
            data-hook={RentalsListDataHooks.attributeNumberRange}
          />
        </section>
      );
    }),

    checkboxSection(
      t('widget.servicesList.filters.rentalBasisTitle'),
      'rentalBasis',
      [
        {
          id: 'hourly',
          label: t('widget.servicesList.filters.rentalBasisHourly'),
        },
        {
          id: 'daily',
          label: t('widget.servicesList.filters.rentalBasisDaily'),
        },
      ],
      RentalsListDataHooks.rentalBasisCheckbox,
    ),

    checkboxSection(
      t('widget.servicesList.filters.paymentOptionsTitle'),
      'paymentOptions',
      [
        {
          id: 'inPerson',
          label: t('widget.servicesList.filters.paymentInPerson'),
        },
        {
          id: 'online',
          label: t('widget.servicesList.filters.paymentOnline'),
        },
      ],
      RentalsListDataHooks.paymentOptionCheckbox,
    ),
  ].filter(Boolean);

  /*
   * The builder draws an explicit rule *between* sections rather than a
   * border on each, so the line lands midway in the body gap. Interleave the
   * same way.
   */
  const body = (
    <div className={classes.body}>
      {sections.map((section, i) => (
        <React.Fragment key={i}>
          {i > 0 && <Divider className={classes.sectionDivider} ariaHidden />}
          {section}
        </React.Fragment>
      ))}
    </div>
  );

  const footer = (
    <div className={classes.footer}>
      <TextButton
        priority={TextButtonPriority.primary}
        className={classes.clearAll}
        disabled={!hasActiveFilters}
        onClick={() => {
          logger.clickCta({ componentName: 'clear_filters' });
          setDraft((d) => ({
            ...d,
            attributes: emptyAttributes,
            rentalBasis: [],
            paymentOptions: [],
          }));
        }}
        data-hook={RentalsListDataHooks.filtersModalClearAll}
      >
        {t('widget.servicesList.filters.clearAll')}
      </TextButton>
      <Button
        priority={ButtonPriority.primary}
        paddingMode={PaddingModes.explicitPaddings}
        className={classes.apply}
        onClick={() => {
          logger.clickCta({ componentName: 'apply_filters' });
          onApply(draft);
        }}
        data-hook={RentalsListDataHooks.filtersModalApply}
      >
        {t('widget.servicesList.filters.apply')}
      </Button>
    </div>
  );

  const content = (
    <div className={classes.root} data-hook={RentalsListDataHooks.filtersModal}>
      <div className={classes.header}>
        <Text
          className={classes.title}
          typography={TYPOGRAPHY.smallTitle}
          tagName="h1"
        >
          {t('widget.servicesList.filters.filtersButton')}
        </Text>

        {/* Desktop gets `Dialog`'s own close button; the drawer has none. */}
        {isMobile ? (
          <button
            type="button"
            className={classes.close}
            data-hook={RentalsListDataHooks.filtersModalClose}
            aria-label={t('widget.general.close.button.label')}
            onClick={onClose}
          >
            <Dismiss />
          </button>
        ) : null}
      </div>
      {body}
      {footer}
    </div>
  );

  if (isMobile) {
    return popoverLayer
      ? createPortal(
          <MobileDrawer
            isOpen={isOpen}
            onRequestClose={onClose}
            className={classes.drawer}
          >
            {content}
          </MobileDrawer>,
          popoverLayer,
        )
      : null;
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      wiredToSiteColors
      className={classes.dialog}
      contentClassName={classes.dialogContent}
      childrenWrapperClassName={classes.childrenWrapper}
    >
      {content}
    </Dialog>
  );
};
